import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { UserType } from '../../types';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password);

      // Block staff from customer login
      if (userData.user_type === UserType.STAFF) {
        setError('This login is for customers only. Admin users should use the admin login.');
        setLoading(false);
        // Clear the logged in user
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return;
      }

      navigate('/customer/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Welcome Back</h2>
            <p className="auth-subtitle">Sign in to access your account and manage your loans</p>
          </div>

          {error && <ErrorMessage message={error} onClose={() => setError('')} />}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label htmlFor="password">Password</label>
                <Link to="/forgot-password" className="auth-link" style={{ fontSize: '0.875rem' }}>
                  Forgot Password?
                </Link>
              </div>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              Sign In
            </Button>
          </form>

          <div className="auth-footer">
            <p className="auth-link-text">
              Don't have an account? <Link to="/register" className="auth-link">Create one now</Link>
            </p>
            <div className="auth-divider"></div>
            <p className="auth-link-text auth-link-secondary">
              Admin user? <Link to="/admin/login" className="auth-link">Admin Portal</Link>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .auth-container {
            padding: 2rem 1.5rem;
          }

          .auth-card {
            padding: 2rem;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .auth-page {
            padding: 1.5rem;
          }

          .auth-container {
            padding: 1rem;
          }

          .auth-card {
            padding: 1.5rem;
          }

          .auth-header h2 {
            font-size: 1.75rem;
          }

          .auth-subtitle {
            font-size: 0.9375rem;
          }

          .form-group label {
            font-size: 0.875rem;
          }

          .form-group input {
            padding: 0.875rem;
            font-size: 1rem;
          }

          .auth-link-text {
            font-size: 0.875rem;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .auth-page {
            padding: 1rem;
          }

          .auth-card {
            padding: 1.25rem;
          }

          .auth-header h2 {
            font-size: 1.5rem;
          }

          .form-group input {
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};
